import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SetRow } from "@/hooks/useProducts";
import FeaturedCarouselCard from "./FeaturedCarouselCard";
import { useTranslation } from "react-i18next";

interface FeaturedProductsDesktopProps {
  sets: SetRow[];
}

export const FeaturedProductsDesktop = ({ sets }: FeaturedProductsDesktopProps) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [manualOffset, setManualOffset] = useState(0);

  // Duplicamos la lista de sets (4 veces) para tener un bucle infinito continuo e impecable
  const duplicatedSets = [...sets, ...sets, ...sets, ...sets];

  // Desplazamiento manual mediante controles
  const handleScroll = (direction: "left" | "right") => {
    const cardWidth = 300 + 24; // card width + gap
    const scrollAmount = direction === "left" ? -cardWidth * 2 : cardWidth * 2;
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <div className="relative">
      {/* Controles de navegación y estado */}
      <div className="flex items-center justify-between mb-4 px-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium bg-muted/60 px-2.5 py-1 rounded-full border border-border/50">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Últimas incorporaciones a la comunidad
          </span>
          <button
            onClick={() => setIsPaused((prev) => !prev)}
            aria-label={isPaused ? "Reanudar carrusel" : "Pausar carrusel"}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 p-1 rounded-md hover:bg-muted transition-colors"
            title={isPaused ? "Reanudar movimiento" : "Pausar movimiento"}
          >
            {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            <span className="sr-only">{isPaused ? "Play" : "Pause"}</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full shadow-sm hover:bg-muted"
            onClick={() => handleScroll("left")}
            aria-label="Anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full shadow-sm hover:bg-muted"
            onClick={() => handleScroll("right")}
            aria-label="Siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Contenedor del Carrusel con máscaras de difuminado a los lados */}
      <div
        className="relative w-full overflow-hidden rounded-2xl group"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Máscara de degradado izquierda */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-background via-background/80 to-transparent" />

        {/* Pista de desplazamiento continuo con pausa al hover */}
        <div
          ref={trackRef}
          className={`flex gap-6 overflow-x-auto no-scrollbar scroll-smooth py-3 px-6 ${
            isPaused ? "" : "animate-marquee"
          }`}
          style={{
            animationDuration: `${Math.max(sets.length * 6, 40)}s`,
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        >
          {duplicatedSets.map((set, index) => (
            <div
              key={`${set.id}-${index}`}
              className="flex-shrink-0 w-[280px] lg:w-[300px]"
            >
              <FeaturedCarouselCard set={set} />
            </div>
          ))}
        </div>

        {/* Máscara de degradado derecha */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-background via-background/80 to-transparent" />
      </div>
    </div>
  );
};

export default FeaturedProductsDesktop;
